import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminOptimize() {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Form states
  const [newAuthPub, setNewAuthPub] = useState("");
  const [newNonAuthPub, setNewNonAuthPub] = useState("");
  const [newMainstreamPub, setNewMainstreamPub] = useState("");
  const [newLocalPub, setNewLocalPub] = useState("");
  const [newTradePub, setNewTradePub] = useState("");
  const [newKeyword, setNewKeyword] = useState("");
  const [keywordLocations, setKeywordLocations] = useState({ metadata: false, title: false, url: false });
  
  // Custom condition form states
  const [newCondition, setNewCondition] = useState({
    criterion: "publication_authority",
    matchType: "publication",
    matchValue: "",
    status: "pass",
    customMessage: ""
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem("wiki_token");
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/admin/settings?token=${token}`);
      // Ensure all required nested objects exist
      const data = response.data || {};
      setSettings({
        ...data,
        publication_authority: data.publication_authority || { authoritative: [], not_authoritative: [] },
        mainstream_classification: data.mainstream_classification || { mainstream: [], local: [], trade_press: [] },
        primary_focus: data.primary_focus || { strictness: 50 },
        in_depth_profile: data.in_depth_profile || { words_per_point: 100 },
        independence: data.independence || { prohibited_keywords: [] },
        custom_conditions: data.custom_conditions || []
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    const token = localStorage.getItem("wiki_token");
    if (!token) return;

    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/settings?token=${token}`, settings);
      toast.success("Settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const addPublication = (category, subcategory, value) => {
    if (!value || !value.trim()) {
      toast.error("Please enter a publication name");
      return;
    }
    
    setSettings(prev => {
      if (!prev) return prev;
      
      const currentCategory = prev[category] || {};
      const currentList = currentCategory[subcategory] || [];
      
      // Check for duplicates
      if (currentList.includes(value.trim())) {
        toast.error("Publication already exists in this list");
        return prev;
      }
      
      return {
        ...prev,
        [category]: {
          ...currentCategory,
          [subcategory]: [...currentList, value.trim()]
        }
      };
    });
    
    toast.success(`Added "${value.trim()}" to the list`);
  };

  const removePublication = (category, subcategory, index) => {
    setSettings(prev => {
      if (!prev || !prev[category] || !prev[category][subcategory]) return prev;
      
      return {
        ...prev,
        [category]: {
          ...prev[category],
          [subcategory]: prev[category][subcategory].filter((_, i) => i !== index)
        }
      };
    });
  };

  const addKeyword = () => {
    if (!newKeyword.trim()) {
      toast.error("Please enter a keyword");
      return;
    }
    
    const locations = Object.entries(keywordLocations)
      .filter(([_, v]) => v)
      .map(([k]) => k);
    
    if (locations.length === 0) {
      toast.error("Please select at least one location");
      return;
    }
    
    setSettings(prev => {
      if (!prev) return prev;
      
      const independence = prev.independence || {};
      const keywords = independence.prohibited_keywords || [];
      
      return {
        ...prev,
        independence: {
          ...independence,
          prohibited_keywords: [...keywords, { keyword: newKeyword.trim(), locations }]
        }
      };
    });
    
    setNewKeyword("");
    setKeywordLocations({ metadata: false, title: false, url: false });
    toast.success("Keyword added");
  };

  const removeKeyword = (index) => {
    setSettings(prev => {
      if (!prev || !prev.independence || !prev.independence.prohibited_keywords) return prev;
      
      return {
        ...prev,
        independence: {
          ...prev.independence,
          prohibited_keywords: prev.independence.prohibited_keywords.filter((_, i) => i !== index)
        }
      };
    });
  };

  const addCustomCondition = () => {
    if (!newCondition.matchValue.trim()) {
      toast.error("Please enter a match value");
      return;
    }
    if (!newCondition.customMessage.trim()) {
      toast.error("Please enter a custom message");
      return;
    }
    
    setSettings(prev => {
      if (!prev) return prev;
      
      const conditions = prev.custom_conditions || [];
      
      return {
        ...prev,
        custom_conditions: [...conditions, { ...newCondition, id: Date.now() }]
      };
    });
    
    setNewCondition({
      criterion: "publication_authority",
      matchType: "publication",
      matchValue: "",
      status: "pass",
      customMessage: ""
    });
    toast.success("Custom condition added");
  };

  const removeCustomCondition = (index) => {
    setSettings(prev => {
      if (!prev || !prev.custom_conditions) return prev;
      
      return {
        ...prev,
        custom_conditions: prev.custom_conditions.filter((_, i) => i !== index)
      };
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#72777d]">Loading settings...</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">Failed to load settings. Please refresh the page.</div>
      </div>
    );
  }

  const criterionOptions = [
    { value: "publication_authority", label: "1. Publication Authority" },
    { value: "mainstream_national", label: "2. Mainstream National" },
    { value: "primary_topic", label: "3. Primary Topic" },
    { value: "in_depth", label: "4. In-Depth Profile" },
    { value: "independence", label: "5. Independence" }
  ];

  const matchTypeOptions = [
    { value: "publication", label: "Publication Name" },
    { value: "byline", label: "Byline/Author" },
    { value: "keyword", label: "Keyword in Content" },
    { value: "url", label: "URL Contains" }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-wiki text-2xl text-[#202122]">Optimize Review Logic</h1>
        <button
          onClick={saveSettings}
          disabled={isSaving}
          className="flex items-center gap-2 bg-[#3366cc] text-white px-4 py-2 rounded hover:bg-[#2a4b8d] disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {isSaving ? "Saving..." : "Save All Changes"}
        </button>
      </div>

      <div className="space-y-6">
        {/* 1. Publication Authority */}
        <div className="bg-white border border-[#a2a9b1] rounded p-4">
          <h2 className="font-wiki text-lg text-[#202122] mb-4">1. Publication Authority</h2>
          <p className="text-sm text-[#72777d] mb-4">
            Override the automatic publication reliability check by marking specific publications as authoritative or not.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Authoritative */}
            <div>
              <h3 className="font-medium text-[#202122] mb-2">Authoritative (Pass)</h3>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newAuthPub}
                  onChange={(e) => setNewAuthPub(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addPublication("publication_authority", "authoritative", newAuthPub);
                      setNewAuthPub("");
                    }
                  }}
                  placeholder="Publication name"
                  className="flex-1 px-3 py-1 border border-[#a2a9b1] rounded text-sm"
                />
                <button
                  onClick={() => {
                    addPublication("publication_authority", "authoritative", newAuthPub);
                    setNewAuthPub("");
                  }}
                  className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                  title="Add publication"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(settings.publication_authority?.authoritative || []).map((pub, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-green-50 px-2 py-1 rounded text-sm">
                    <span className="text-green-800">{pub}</span>
                    <button onClick={() => removePublication("publication_authority", "authoritative", idx)}>
                      <Trash2 className="w-4 h-4 text-red-500 hover:text-red-700" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Not Authoritative */}
            <div>
              <h3 className="font-medium text-[#202122] mb-2">Not Authoritative (Fail)</h3>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newNonAuthPub}
                  onChange={(e) => setNewNonAuthPub(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addPublication("publication_authority", "not_authoritative", newNonAuthPub);
                      setNewNonAuthPub("");
                    }
                  }}
                  placeholder="Publication name"
                  className="flex-1 px-3 py-1 border border-[#a2a9b1] rounded text-sm"
                />
                <button
                  onClick={() => {
                    addPublication("publication_authority", "not_authoritative", newNonAuthPub);
                    setNewNonAuthPub("");
                  }}
                  className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                  title="Add publication"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(settings.publication_authority?.not_authoritative || []).map((pub, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-red-50 px-2 py-1 rounded text-sm">
                    <span className="text-red-800">{pub}</span>
                    <button onClick={() => removePublication("publication_authority", "not_authoritative", idx)}>
                      <Trash2 className="w-4 h-4 text-red-500 hover:text-red-700" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 2. Mainstream Classification */}
        <div className="bg-white border border-[#a2a9b1] rounded p-4">
          <h2 className="font-wiki text-lg text-[#202122] mb-4">2. Mainstream National Publication</h2>
          <p className="text-sm text-[#72777d] mb-4">
            Classify publications as mainstream (pass), local (fail), or trade press (fail).
          </p>
          
          <div className="grid grid-cols-3 gap-4">
            {/* Mainstream */}
            <div>
              <h3 className="font-medium text-[#202122] mb-2">Mainstream (Pass)</h3>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newMainstreamPub}
                  onChange={(e) => setNewMainstreamPub(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addPublication("mainstream_classification", "mainstream", newMainstreamPub);
                      setNewMainstreamPub("");
                    }
                  }}
                  placeholder="Publication name"
                  className="flex-1 px-2 py-1 border border-[#a2a9b1] rounded text-sm"
                />
                <button
                  onClick={() => {
                    addPublication("mainstream_classification", "mainstream", newMainstreamPub);
                    setNewMainstreamPub("");
                  }}
                  className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                  title="Add publication"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(settings.mainstream_classification?.mainstream || []).map((pub, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-green-50 px-2 py-1 rounded text-sm">
                    <span className="text-green-800 truncate">{pub}</span>
                    <button onClick={() => removePublication("mainstream_classification", "mainstream", idx)}>
                      <Trash2 className="w-4 h-4 text-red-500 hover:text-red-700" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Local */}
            <div>
              <h3 className="font-medium text-[#202122] mb-2">Local (Fail)</h3>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newLocalPub}
                  onChange={(e) => setNewLocalPub(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addPublication("mainstream_classification", "local", newLocalPub);
                      setNewLocalPub("");
                    }
                  }}
                  placeholder="Publication name"
                  className="flex-1 px-2 py-1 border border-[#a2a9b1] rounded text-sm"
                />
                <button
                  onClick={() => {
                    addPublication("mainstream_classification", "local", newLocalPub);
                    setNewLocalPub("");
                  }}
                  className="p-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                  title="Add publication"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(settings.mainstream_classification?.local || []).map((pub, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-yellow-50 px-2 py-1 rounded text-sm">
                    <span className="text-yellow-800 truncate">{pub}</span>
                    <button onClick={() => removePublication("mainstream_classification", "local", idx)}>
                      <Trash2 className="w-4 h-4 text-red-500 hover:text-red-700" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Trade Press */}
            <div>
              <h3 className="font-medium text-[#202122] mb-2">Trade Press (Fail)</h3>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newTradePub}
                  onChange={(e) => setNewTradePub(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addPublication("mainstream_classification", "trade_press", newTradePub);
                      setNewTradePub("");
                    }
                  }}
                  placeholder="Publication name"
                  className="flex-1 px-2 py-1 border border-[#a2a9b1] rounded text-sm"
                />
                <button
                  onClick={() => {
                    addPublication("mainstream_classification", "trade_press", newTradePub);
                    setNewTradePub("");
                  }}
                  className="p-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                  title="Add publication"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(settings.mainstream_classification?.trade_press || []).map((pub, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-orange-50 px-2 py-1 rounded text-sm">
                    <span className="text-orange-800 truncate">{pub}</span>
                    <button onClick={() => removePublication("mainstream_classification", "trade_press", idx)}>
                      <Trash2 className="w-4 h-4 text-red-500 hover:text-red-700" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 3. Primary Focus */}
        <div className="bg-white border border-[#a2a9b1] rounded p-4">
          <h2 className="font-wiki text-lg text-[#202122] mb-4">3. Primary Focus (AI Analysis)</h2>
          <p className="text-sm text-[#72777d] mb-4">
            Adjust how strictly the AI analyzes whether the topic is the primary subject of an article.
          </p>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#72777d]">Lenient</span>
            <input
              type="range"
              min="0"
              max="100"
              value={settings.primary_focus?.strictness || 50}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                primary_focus: { ...prev.primary_focus, strictness: parseInt(e.target.value) }
              }))}
              className="flex-1"
            />
            <span className="text-sm text-[#72777d]">Strict</span>
            <span className="text-sm font-medium w-12 text-center">{settings.primary_focus?.strictness || 50}%</span>
          </div>
        </div>

        {/* 4. In-Depth Profile */}
        <div className="bg-white border border-[#a2a9b1] rounded p-4">
          <h2 className="font-wiki text-lg text-[#202122] mb-4">4. In-Depth Profile (Word Count)</h2>
          <p className="text-sm text-[#72777d] mb-4">
            Set the minimum words required per score point. Lower values = easier to pass.
          </p>
          
          <div className="flex items-center gap-4">
            <label className="text-sm text-[#72777d]">Words per point:</label>
            <input
              type="number"
              min="50"
              max="500"
              value={settings.in_depth_profile?.words_per_point || 100}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                in_depth_profile: { ...prev.in_depth_profile, words_per_point: parseInt(e.target.value) || 100 }
              }))}
              className="w-24 px-3 py-1 border border-[#a2a9b1] rounded"
            />
            <span className="text-sm text-[#72777d]">
              (Current: {Math.floor(500 / (settings.in_depth_profile?.words_per_point || 100))}/5 points for 500 words)
            </span>
          </div>
        </div>

        {/* 5. Independence */}
        <div className="bg-white border border-[#a2a9b1] rounded p-4">
          <h2 className="font-wiki text-lg text-[#202122] mb-4">5. Independence Check</h2>
          <p className="text-sm text-[#72777d] mb-4">
            Add keywords that indicate non-independent content (press releases, sponsored content, etc.).
          </p>
          
          <div className="flex items-center gap-4 mb-4">
            <input
              type="text"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addKeyword();
              }}
              placeholder="Keyword to detect"
              className="flex-1 px-3 py-2 border border-[#a2a9b1] rounded"
            />
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={keywordLocations.metadata}
                  onChange={(e) => setKeywordLocations(prev => ({ ...prev, metadata: e.target.checked }))}
                />
                Metadata
              </label>
              <label className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={keywordLocations.title}
                  onChange={(e) => setKeywordLocations(prev => ({ ...prev, title: e.target.checked }))}
                />
                Title
              </label>
              <label className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={keywordLocations.url}
                  onChange={(e) => setKeywordLocations(prev => ({ ...prev, url: e.target.checked }))}
                />
                URL
              </label>
            </div>
            <button
              onClick={addKeyword}
              className="flex items-center gap-1 px-3 py-2 bg-[#3366cc] text-white rounded hover:bg-[#2a4b8d]"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
          
          <div className="space-y-2">
            {(settings.independence?.prohibited_keywords || []).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between bg-[#f8f9fa] px-3 py-2 rounded">
                <div>
                  <span className="font-medium text-[#202122]">"{item.keyword}"</span>
                  <span className="text-sm text-[#72777d] ml-2">
                    in: {item.locations?.join(", ")}
                  </span>
                </div>
                <button onClick={() => removeKeyword(idx)}>
                  <Trash2 className="w-4 h-4 text-red-500 hover:text-red-700" />
                </button>
              </div>
            ))}
            {(!settings.independence?.prohibited_keywords || settings.independence.prohibited_keywords.length === 0) && (
              <div className="text-sm text-[#72777d] italic">No prohibited keywords configured.</div>
            )}
          </div>
        </div>

        {/* Custom Conditions */}
        <div className="bg-white border border-[#a2a9b1] rounded p-4">
          <h2 className="font-wiki text-lg text-[#202122] mb-2 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            Custom Conditions
          </h2>
          <p className="text-sm text-[#72777d] mb-4">
            Create custom rules that trigger specific messages when certain conditions are met (e.g., byline contains "Contributor", publication is "Forbes", or content contains specific keywords).
          </p>
          
          <div className="bg-[#f8f9fa] p-4 rounded mb-4">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-[#202122] mb-1">Criterion to Affect</label>
                <select
                  value={newCondition.criterion}
                  onChange={(e) => setNewCondition(prev => ({ ...prev, criterion: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#a2a9b1] rounded text-sm"
                >
                  {criterionOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#202122] mb-1">Result Status</label>
                <select
                  value={newCondition.status}
                  onChange={(e) => setNewCondition(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#a2a9b1] rounded text-sm"
                >
                  <option value="pass">Pass (Green)</option>
                  <option value="fail">Fail (Red)</option>
                  <option value="undecided">Undecided (Yellow)</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-[#202122] mb-1">Match Type</label>
                <select
                  value={newCondition.matchType}
                  onChange={(e) => setNewCondition(prev => ({ ...prev, matchType: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#a2a9b1] rounded text-sm"
                >
                  {matchTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#202122] mb-1">Match Value (case-insensitive)</label>
                <input
                  type="text"
                  value={newCondition.matchValue}
                  onChange={(e) => setNewCondition(prev => ({ ...prev, matchValue: e.target.value }))}
                  placeholder={newCondition.matchType === 'publication' ? 'e.g., Forbes' : newCondition.matchType === 'byline' ? 'e.g., Contributor' : 'e.g., sponsored'}
                  className="w-full px-3 py-2 border border-[#a2a9b1] rounded text-sm"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-[#202122] mb-1">Custom Message</label>
              <textarea
                value={newCondition.customMessage}
                onChange={(e) => setNewCondition(prev => ({ ...prev, customMessage: e.target.value }))}
                placeholder="Enter the custom message to display when this condition is met..."
                rows={2}
                className="w-full px-3 py-2 border border-[#a2a9b1] rounded text-sm"
              />
            </div>
            
            <button
              onClick={addCustomCondition}
              className="flex items-center gap-1 px-4 py-2 bg-[#3366cc] text-white rounded hover:bg-[#2a4b8d]"
            >
              <Plus className="w-4 h-4" />
              Add Custom Condition
            </button>
          </div>
          
          <div className="space-y-2">
            {(settings.custom_conditions || []).map((cond, idx) => (
              <div key={cond.id || idx} className="flex items-start justify-between bg-[#f8f9fa] px-4 py-3 rounded border border-[#ddd]">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      cond.status === 'pass' ? 'bg-green-100 text-green-800' :
                      cond.status === 'fail' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {cond.status.toUpperCase()}
                    </span>
                    <span className="text-sm font-medium text-[#202122]">
                      {criterionOptions.find(c => c.value === cond.criterion)?.label}
                    </span>
                  </div>
                  <div className="text-sm text-[#72777d] mb-1">
                    When <strong>{matchTypeOptions.find(m => m.value === cond.matchType)?.label}</strong> contains "<strong>{cond.matchValue}</strong>"
                  </div>
                  <div className="text-sm text-[#202122] bg-white p-2 rounded border border-[#eee]">
                    {cond.customMessage}
                  </div>
                </div>
                <button onClick={() => removeCustomCondition(idx)} className="ml-4">
                  <Trash2 className="w-4 h-4 text-red-500 hover:text-red-700" />
                </button>
              </div>
            ))}
            {(!settings.custom_conditions || settings.custom_conditions.length === 0) && (
              <div className="text-sm text-[#72777d] italic">No custom conditions configured.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
